/**
 * Material Configurations for SMBeamDesigner
 * 
 * Contains all material grades, properties, and helper functions
 */

// Steel grades with properties
export const steelGrades = {
  S235: {
    E: 210e9, // Pa
    density: 7850, // kg/m³
    fy: 235e6, // Pa
    fu: 360e6, // Pa
    availableSubgrades: ['JR', 'J0', 'J2'],
    thicknessBands: [40, 80, 100] // mm
  },
  S275: {
    E: 210e9,
    density: 7850,
    fy: 275e6,
    fu: 430e6,
    availableSubgrades: ['JR', 'J0', 'J2'],
    thicknessBands: [40, 80, 100]
  },
  S355: {
    E: 210e9,
    density: 7850,
    fy: 355e6,
    fu: 510e6,
    availableSubgrades: ['JR', 'J0', 'J2', 'K2'],
    thicknessBands: [40, 80, 100]
  },
  S420: {
    E: 210e9,
    density: 7850,
    fy: 420e6,
    fu: 520e6,
    availableSubgrades: ['M', 'ML'],
    thicknessBands: [40, 80, 100]
  },
  S460: {
    E: 210e9,
    density: 7850,
    fy: 460e6,
    fu: 550e6,
    availableSubgrades: ['M', 'ML'],
    thicknessBands: [40, 80, 100]
  }
}

// Concrete classes
export const concreteClasses = {
  'C12/15': { fck: 12e6, E: 27e9, density: 2400 },
  'C16/20': { fck: 16e6, E: 29e9, density: 2400 },
  'C20/25': { fck: 20e6, E: 30e9, density: 2400 },
  'C25/30': { fck: 25e6, E: 31e9, density: 2400 },
  'C30/37': { fck: 30e6, E: 33e9, density: 2400 },
  'C35/45': { fck: 35e6, E: 34e9, density: 2400 },
  'C40/50': { fck: 40e6, E: 35e9, density: 2400 },
  'C45/55': { fck: 45e6, E: 36e9, density: 2400 },
  'C50/60': { fck: 50e6, E: 37e9, density: 2400 },
  'C55/67': { fck: 55e6, E: 38e9, density: 2400 },
  'C60/75': { fck: 60e6, E: 39e9, density: 2400 },
  'C70/85': { fck: 70e6, E: 41e9, density: 2400 },
  'C80/95': { fck: 80e6, E: 42e9, density: 2400 },
  'C90/105': { fck: 90e6, E: 44e9, density: 2400 }
}

// Reinforcement steel classes (EC2 + Norwegian NS 3576-3)
export const reinforcementClasses = {
  B500NC: { fy: 500e6, Es: 200e9 },  // Norwegian standard, ductility C
  B500NB: { fy: 500e6, Es: 200e9 },  // Norwegian standard, ductility B
  B500A: { fy: 500e6, Es: 200e9 },
  B500B: { fy: 500e6, Es: 200e9 },
  B500C: { fy: 500e6, Es: 200e9 }
}

// Prestressing steel classes
export const prestressClasses = {
  Y1770: { fpk: 1770e6, Ep: 195e9 },
  Y1860: { fpk: 1860e6, Ep: 195e9 }
}

// Timber fallback properties — used only when the materials DB is unreachable.
// E is E0,mean and density is ρ_mean, per EN 14080:2013 (glulam) / EN 338:2016
// (solid). The authoritative full list lives in en338_timber_classes.json.
// γM is not stored here: it depends on the national annex — see getTimberGammaM.
export const timberGlulamFallback = {
  GL24h: { E: 11500e6, density: 420 },
  GL28h: { E: 12600e6, density: 460 },
  GL30h: { E: 13600e6, density: 480 },
  GL32h: { E: 14200e6, density: 490 }
}

export const timberSolidFallback = {
  C18: { E: 9000e6, density: 380 },
  C24: { E: 11000e6, density: 420 },
  C30: { E: 12000e6, density: 460 },
  C35: { E: 13000e6, density: 470 }
}

// Helper functions
export const getAlphaCC = (nationalAnnex: 'Standard' | 'Norwegian'): number => {
  return nationalAnnex === 'Norwegian' ? 0.85 : 1.0
}

// γM for timber per (kind, annex). NS-EN 1995-1-1 NA Tabell NA.2.3: glulam 1.15, solid 1.25.
// Base-EC Table 2.3: glulam 1.25, solid 1.30. Mirrors backend Ec5MaterialFactors.
export const getTimberGammaM = (kind: 'glulam' | 'solid', nationalAnnex: 'Standard' | 'Norwegian'): number => {
  if (kind === 'glulam') return nationalAnnex === 'Norwegian' ? 1.15 : 1.25
  return nationalAnnex === 'Norwegian' ? 1.25 : 1.30
}

// Build resolved material payload for backend
// NOTE: This function should ONLY be called after materialDetails has been successfully fetched from database
// Fallback values have been removed - database values are required
export const resolveMaterialPayload = (params: {
  currentMaterial: string
  nationalAnnex: 'Standard' | 'Norwegian'
  materialDetails: any  // Required: fetched from database
  selectedSteelGrade?: string
  selectedSteelSubgrade?: string
  steelThickness?: number | ''
  selectedConcreteClass?: string
  selectedReinfClass?: string
  selectedPrestressClass?: string
  timberKind?: string
  selectedTimberClass?: string
  timberLoadDuration?: string
  timberServiceClass?: 1 | 2 | 3
  concreteRelativeHumidity?: number
  concreteNotionalSize?: number
  concreteLoadingAge?: number
  concreteCreepCoefficient?: number
  concreteEffectiveModulus?: number
}) => {
  const {
    currentMaterial,
    nationalAnnex,
    materialDetails,
    selectedSteelGrade = 'S355',
    selectedSteelSubgrade = 'J0',
    steelThickness = '',
    selectedConcreteClass = 'C30/37',
    selectedReinfClass = 'B500NC',
    selectedPrestressClass = 'Y1860',
    timberKind = 'glulam',
    selectedTimberClass = 'GL24h',
    timberLoadDuration = 'medium-term',
    timberServiceClass = 2,
    concreteRelativeHumidity = 50,
    concreteNotionalSize = 200,
    concreteLoadingAge = 28,
    concreteCreepCoefficient = 2.5,
    concreteEffectiveModulus = 0
  } = params

  // Check if materialDetails is available
  if (!materialDetails || materialDetails.error) {
    throw new Error('Material properties must be loaded from database. Please ensure a valid material is selected.')
  }

  if (currentMaterial === 'steel') {
    if (!materialDetails.yieldStrength || !materialDetails.ultimateStrength || !materialDetails.elasticModulus) {
      throw new Error('Incomplete steel material data from database.')
    }
    return {
      nationalAnnex,
      materialType: 'steel',
      elasticModulus: materialDetails.elasticModulus,
      density: materialDetails.density || 7850,
      steelGrade: selectedSteelGrade,
      steelSubgrade: materialDetails.subgrade || selectedSteelSubgrade,
      steelProductForm: materialDetails.productForm || 'section',
      steelThicknessMax: materialDetails.thicknessMax ?? (steelThickness === '' ? undefined : steelThickness),
      steelYieldStrength: materialDetails.yieldStrength,
      steelUltimateStrength: materialDetails.ultimateStrength,
      steelUltimateStrengthMin: materialDetails.ultimateStrengthMin,
      steelUltimateStrengthMax: materialDetails.ultimateStrengthMax,
      steelFyd: materialDetails.fyd,
      steelFud: materialDetails.fud,
      steelGammaM0: materialDetails.gammaM0,
      steelGammaM1: materialDetails.gammaM1,
      steelGammaM2: materialDetails.gammaM2,
      steelPoissonRatio: materialDetails.poissonRatio
    }
  }
  
  if (currentMaterial === 'concrete') {
    if (!materialDetails.fck || !materialDetails.elasticModulus) {
      throw new Error('Incomplete concrete material data from database.')
    }
    if (!materialDetails.reinforcement?.fyk) {
      throw new Error('Incomplete reinforcement steel data from database.')
    }
    if (!materialDetails.prestressing?.fpk) {
      throw new Error('Incomplete prestressing steel data from database.')
    }
    return {
      nationalAnnex,
      materialType: 'concrete',
      elasticModulus: materialDetails.elasticModulus, // Pa
      density: materialDetails.density || 2400,
      concreteClass: selectedConcreteClass,
      concreteFck: materialDetails.fck, // MPa (backend now uses MPa)
      reinforcementClass: selectedReinfClass,
      reinforcementFy: materialDetails.reinforcement.fyk, // MPa (backend now uses MPa)
      prestressClass: selectedPrestressClass,
      prestressFpk: materialDetails.prestressing.fpk, // MPa (backend now uses MPa)
      prestressElasticModulus: materialDetails.prestressing.elasticModulus, // Pa (backend returns elasticModulus)
      // Creep parameters (EC2 Annex B)
      concreteRelativeHumidity,
      concreteNotionalSize,
      concreteLoadingAge,
      concreteCreepCoefficient,
      concreteEffectiveModulus
    }
  }
  
  // Timber
  if (!materialDetails.e0Mean || !materialDetails.density || !materialDetails.kmod || !materialDetails.gammaM) {
    throw new Error('Incomplete timber material data from database.')
  }

  const timberGammaM = materialDetails.gammaM as number
  const timberKmodValue = materialDetails.kmod as number

  const fmCharacteristic = materialDetails.fb_d && timberKmodValue > 0
    ? (materialDetails.fb_d * timberGammaM) / timberKmodValue
    : undefined
  const ft0Characteristic = materialDetails.ft0_d && timberKmodValue > 0
    ? (materialDetails.ft0_d * timberGammaM) / timberKmodValue
    : undefined
  const ft90Characteristic = materialDetails.ft90_d && timberKmodValue > 0
    ? (materialDetails.ft90_d * timberGammaM) / timberKmodValue
    : undefined
  const fc0Characteristic = materialDetails.fc0_d && timberKmodValue > 0
    ? (materialDetails.fc0_d * timberGammaM) / timberKmodValue
    : undefined
  const fc90Characteristic = materialDetails.fc90_d && timberKmodValue > 0
    ? (materialDetails.fc90_d * timberGammaM) / timberKmodValue
    : undefined
  const fvCharacteristic = materialDetails.fv_d && timberKmodValue > 0
    ? (materialDetails.fv_d * timberGammaM) / timberKmodValue
    : undefined
  const frCharacteristic = materialDetails.fr_d && timberKmodValue > 0
    ? (materialDetails.fr_d * timberGammaM) / timberKmodValue
    : undefined

  return {
    nationalAnnex,
    materialType: 'timber',
    elasticModulus: materialDetails.e0Mean,
    density: materialDetails.density,
    timberKind,
    timberClass: selectedTimberClass,
    timberGammaM: timberGammaM,
    timberGammaMSolid: getTimberGammaM('solid', nationalAnnex),
    timberLoadDuration,
    timberGammaMDesign: timberGammaM,
    timberFbDesign: materialDetails.fb_d,
    timberFt0Design: materialDetails.ft0_d,
    timberFt90Design: materialDetails.ft90_d,
    timberFc0Design: materialDetails.fc0_d,
    timberFc90Design: materialDetails.fc90_d,
    timberFvDesign: materialDetails.fv_d,
    timberFrDesign: materialDetails.fr_d,
    timberE0Mean: materialDetails.e0Mean,
    timberE90Mean: materialDetails.e90Mean,
    timberGMean: materialDetails.gMean,
    timberFmCharacteristic: fmCharacteristic,
    timberFt0Characteristic: ft0Characteristic,
    timberFt90Characteristic: ft90Characteristic,
    timberFc0Characteristic: fc0Characteristic,
    timberFc90Characteristic: fc90Characteristic,
    timberFvCharacteristic: fvCharacteristic,
    timberFrCharacteristic: frCharacteristic,
    // Creep parameters (EC5)
    timberServiceClass
  }
}


